/**
 * Type-safe error handling for API responses.
 */
import type { AxiosError } from "axios";

export const AUTH_ERROR_CODES = {
  USER_NOT_FOUND: "USER_NOT_FOUND",
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

export interface ApiErrorDetail {
  message: string;
  code?: string;
}

/**
 * Extract error code from API error response.
 * Returns null if no code is present (e.g., for generic errors).
 */
export function getErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const axiosError = error as AxiosError<{ detail?: string | ApiErrorDetail }>;
  const detail = axiosError.response?.data?.detail;

  if (detail && typeof detail === "object" && "code" in detail) {
    return detail.code || null;
  }

  return null;
}

/**
 * Extract error message from API error response.
 * Handles both string and object detail formats.
 */
export function getErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const axiosError = error as AxiosError<{ detail?: string | ApiErrorDetail }>;
  const detail = axiosError.response?.data?.detail;

  if (!detail) {
    return null;
  }

  if (typeof detail === "string") {
    return detail;
  }

  return detail.message || null;
}
